import Phaser from 'phaser';
import { sound } from '../../services/audioService.js';
import { ensureTextures } from '../textureGenerator.js';

export class FitnessGame extends Phaser.Scene {
  constructor() {
    super({ key: 'FitnessGame' });
  }

  init(data) {
    this.difficulty = data.difficulty || 'medium';
    this.barsToAdd = data.barsToAdd || (this.difficulty === 'hard' ? 9 : (this.difficulty === 'medium' ? 6 : 3));
    this.onComplete = data.onComplete;
    this.distanceRan = 0;
    this.isGameOver = false;
    this.canJump = true;

    if (this.difficulty === 'easy') {
      this.targetDistance = 200;
      this.runSpeed = 220;
      this.hurdleIntervalMin = 1400;
      this.hurdleIntervalMax = 2000;
    } else if (this.difficulty === 'medium') {
      this.targetDistance = 350;
      this.runSpeed = 290;
      this.hurdleIntervalMin = 1000;
      this.hurdleIntervalMax = 1600;
    } else {
      this.targetDistance = 500;
      this.runSpeed = 360;
      this.hurdleIntervalMin = 750;
      this.hurdleIntervalMax = 1200;
    }
  }

  create() {
    ensureTextures(this);
    const { width, height } = this.cameras.main;
    const groundY = height - 70;

    // Running Track Background
    this.add.rectangle(width / 2, height / 2, width, height, 0x102A1E);

    // Track Lines
    for (let i = 0; i < 4; i++) {
      this.add.rectangle(width / 2, groundY - 20 - i * 30, width, 1, 0x00E676, 0.15);
    }

    // Ground Floor
    this.ground = this.add.rectangle(width / 2, groundY + 30, width, 60, 0x00C853);
    this.physics.add.existing(this.ground, true);

    // Obstacles Group
    this.hurdles = this.physics.add.group();

    // Runner Character
    this.runner = this.physics.add.sprite(70, groundY - 30, 'particle_star');
    this.runner.setTint(0x00E676);
    this.runner.setScale(1.2);
    this.runner.body.setGravityY(950);
    this.physics.add.collider(this.runner, this.ground, () => {
      this.canJump = true;
    });

    // Collision with Hurdle
    this.physics.add.overlap(this.runner, this.hurdles, (runner, hurdle) => {
      this.hitHurdle();
    });

    // Jump Input
    this.input.on('pointerdown', () => {
      if (this.isGameOver) return;
      if (this.canJump) {
        sound.playJump();
        this.runner.setVelocityY(-460);
        this.canJump = false;
      }
    });

    // HUD
    this.distText = this.add.text(16, 16, `Sprint: ${Math.floor(this.distanceRan)}m / ${this.targetDistance}m`, {
      fontSize: '15px',
      fontStyle: 'bold',
      color: '#00E676'
    });

    this.hintText = this.add.text(width / 2, height - 25, 'Tap to jump over hurdles! 🏃', {
      fontSize: '11px',
      color: 'rgba(255,255,255,0.5)'
    }).setOrigin(0.5);

    // Hurdle Spawner
    this.scheduleNextHurdle();
  }

  scheduleNextHurdle() {
    if (this.isGameOver) return;
    const delay = Phaser.Math.Between(this.hurdleIntervalMin, this.hurdleIntervalMax);
    this.time.delayedCall(delay, () => {
      if (!this.isGameOver) {
        this.spawnHurdle();
        this.scheduleNextHurdle();
      }
    });
  }

  spawnHurdle() {
    const { width, height } = this.cameras.main;
    const groundY = height - 70;
    const hurdle = this.hurdles.create(width + 30, groundY - 16, 'hurdle_bar');
    hurdle.body.allowGravity = false;
    hurdle.body.setImmovable(true);
    hurdle.setVelocityX(-this.runSpeed);
  }

  hitHurdle() {
    sound.playHit();
    this.cameras.main.shake(200, 0.02);
    this.triggerEnd(false);
  }

  update(time, delta) {
    if (this.isGameOver) return;
    const dt = delta / 1000;

    // Progress distance
    this.distanceRan += (this.runSpeed * dt) / 10;
    this.distText.setText(`Sprint: ${Math.floor(this.distanceRan)}m / ${this.targetDistance}m`);

    // Clean up passed hurdles
    this.hurdles.getChildren().forEach((h) => {
      if (h && h.x < -40) {
        h.destroy();
      }
    });

    // Check Win
    if (this.distanceRan >= this.targetDistance) {
      this.triggerEnd(true);
    }
  }

  triggerEnd(isWin) {
    this.isGameOver = true;
    this.hurdles.setVelocityX(0);

    const { width, height } = this.cameras.main;
    this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.8);

    if (isWin) {
      sound.playVictory();
      this.add.text(width / 2, height / 2 - 50, '🏆 FINISH LINE!', {
        fontSize: '24px',
        fontStyle: '800',
        color: '#00E676'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 10, `+${this.barsToAdd} FITNESS BARS GAINED! 🏃`, {
        fontSize: '15px',
        fontStyle: 'bold',
        color: '#00E676'
      }).setOrigin(0.5);

      const finishBtn = this.add.rectangle(width / 2, height / 2 + 50, 140, 42, 0x00E676)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 50, 'CLAIM & EXIT', {
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#000'
      }).setOrigin(0.5);

      finishBtn.on('pointerdown', () => {
        sound.playClick();
        if (this.onComplete) this.onComplete(true);
      });
    } else {
      sound.playGameOver();
      this.add.text(width / 2, height / 2 - 40, '💨 TRIPPED OVER HURDLE', {
        fontSize: '18px',
        fontStyle: '800',
        color: '#FF5252'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 5, `Reached ${Math.floor(this.distanceRan)}m of ${this.targetDistance}m`, {
        fontSize: '13px',
        color: '#A0A5BA'
      }).setOrigin(0.5);

      const retryBtn = this.add.rectangle(width / 2, height / 2 + 45, 120, 38, 0x00E676)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 45, 'RETRY', {
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#000'
      }).setOrigin(0.5);

      retryBtn.on('pointerdown', () => {
        sound.playClick();
        this.scene.restart();
      });
    }
  }
}
